import React, { useState, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CustomerService } from '../../services/customerService';
import { Customer } from '../../types/agent';
import {
  CustomerForm,
  CustomerFormData,
  emptyCustomerFormData,
  validateCustomerForm
} from '../../components/CustomerForm';
import './AddCustomerPage.css';

export const AddCustomerPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentAgent, currentUser } = useAuth();
  const toast = useRef<Toast>(null);

  const [formData, setFormData] = useState<CustomerFormData>({ ...emptyCustomerFormData });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const validateForm = (): boolean => {
    const newErrors = validateCustomerForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fix the errors in the form',
        life: 3000
      });
      return;
    }

    setLoading(true);

    try {
      const customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'> = {
        userId: currentUser?.uid || '',
        assignedAgentId: currentAgent?.id,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        status: formData.status,
        source: formData.source,
        leadScore: formData.leadScore,
        notes: formData.notes.trim(),
        tags: formData.tags,
        profileData: {
          income: formData.income,
          creditScore: formData.creditScore,
          employmentStatus: formData.employmentStatus,
          homeOwnershipStatus: formData.homeOwnershipStatus,
          location: formData.location?.trim()
        },
        mortgageBalance: formData.mortgageBalance,
        homeValue: formData.homeValue
      };

      const customerId = await CustomerService.createCustomer(customerData);

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Customer added successfully',
        life: 3000
      });

      // Navigate to the customer details page after a short delay
      setTimeout(() => {
        navigate(`/agent/customers/${customerId}`);
      }, 1500);

    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to add customer. Please try again.',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: keyof CustomerFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="add-customer-page">
      <Toast ref={toast} />

      <div className="page-header">
        <h1>Add New Customer</h1>
        <p>Enter customer information to add them to your pipeline</p>
      </div>

      <CustomerForm
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/agent/customers')}
        loading={loading}
        submitLabel="Add Customer"
        submitIcon="pi pi-plus"
      />
    </div>
  );
};

export default AddCustomerPage;
